import type { PayloadAction } from "@reduxjs/toolkit";

/**
 * Shared parts of CashRegisterSlice — plan §5.2. Mobile is close to a subset of web: same
 * slice `name`, four identical reducers, and three state fields it shares (one of which is
 * typed differently, see below).
 *
 * Authored per platform instead:
 * - `saveCashSettlementDetails` — genuinely divergent. Web also writes `branchSwitchData` /
 *   `switchUserData` and has a THIRD and FOURTH condition in the `if`
 *   (`close_switch_branch`, and `close_switch_user` + status `open`); mobile has only the
 *   first two. Web's fourth branch reads `CASH_REGISTER_MODAL_TYPES.close_switch_user`,
 *   a key mobile's copy of that constant does not define — see `utils/constants`.
 * - `cashSettlementDetails` — value is `{}` on both sides but the ANNOTATION differs
 *   (web `Record<any, any>`, mobile `{}`), so it is declared per platform rather than
 *   widened here. No shared reducer touches it, so nothing is lost by that.
 * - web's `branchSwitchData` / `switchUserData` / `errorModalConfig` state and its three
 *   extra reducers (`updateCashRegisterSlice`, `updateSettlementIdDetails`,
 *   `updateErrorModalConfig`).
 * - web's `userLogout`/`supportLogout` `extraReducers` (both bodies EMPTY in the app —
 *   preserved); mobile registers none at all.
 *
 * ⚠️ `action` must be `PayloadAction<any>`, never `any` — see the ledger. `any` makes RTK
 * infer the NO-PAYLOAD overload, so every `dispatch(x(payload))` stops compiling. The param
 * name `actions` is the apps' own spelling on this slice; kept.
 *
 * 🗑️ Mobile's app file imports `featureRestrictions` from `helper/featureRestrictionHelper`
 * and `CASH_REGISTER` from `utils/constants` and references NEITHER (ledger hazard 13), so
 * neither is uplifted; both die with the app file.
 */
export interface SharedCashRegisterState {
  showCashRegisterModal: boolean;
  cashRegisterModalType: string;
}

export const sharedInitialState: SharedCashRegisterState = {
  showCashRegisterModal: false,
  cashRegisterModalType: "",
};

export const sharedReducers = {
  // Empty on both sides — these exist only for the sagas' `takeEvery(...)`. Arity preserved.
  openCashRegister: (
    state: SharedCashRegisterState,
    actions: PayloadAction<any>
  ) => {},
  closeCashRegister: (
    state: SharedCashRegisterState,
    actions: PayloadAction<any>
  ) => {},
  getCashSettlementDetails: (
    state: SharedCashRegisterState,
    actions: PayloadAction<any>
  ) => {},
  closeCashRegisterModal: (state: SharedCashRegisterState) => {
    state.cashRegisterModalType = "";
    state.showCashRegisterModal = false;
  },
};
